<template>
  <ProductTable :id="id" :desc="desc" :listMaxDiscountLimitExceededPerZone="listMaxDiscountLimitExceededPerZone"
    :isMaxDiscountLimitExceeded="isMaxDiscountLimitExceeded" :isOfferInvalidForPrices="isOfferInvalidForPrices"
    :areDefaultWeights="areDefaultWeights" :validationErrors="validationErrors" @updatePrice="handlePriceChanged">
    <template v-slot:discounts>
      <DiscountsRow v-if="!isTypeC" :id="id" @updateIsMaxDiscountLimitExceeded="updateIsMaxDiscountLimitExceeded"
        @updateDiscounts="handlePriceChanged" />
    </template>
  </ProductTable>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  GET_PRODUCT_PIVOT_RULES,
  GET_OFFER_ITEM,
  GET_PRODUCT,
  GET_PRODUCT_LIST_UNIT_PRICES,
  GET_PRODUCT_TABLE,
} from '@/store/modules/offer/getters'
import { OFFER_TYPE } from '@/helpers/constants'
import { areDefaultProductWeights } from '@/helpers/offerTypes'
import ProductTable from '@/containers/offer-form/shipping/ProductTable'
import DiscountsRow from '@/containers/offer-form/shipping/DiscountsRow'
import { getZoneErrors } from '@/containers/offer-form/shipping/helpers'
import { UPDATE_PRODUCT_ZONE_KG_PRICE } from '@/store/modules/offer/mutations'

export default {
  name: 'Product',
  components: {
    DiscountsRow,
    ProductTable,
  },
  props: {
    id: {
      type: String,
    },
    desc:{
      type: String,
    }
  },
  data() {
    return {
      listMaxDiscountLimitExceededPerZone: {},
      validationErrors: {},
      isOfferInvalidForZonePrices: false,
      isOfferInvalidForProductPrices: false,
    }
  },
  computed: {
    ...mapGetters('offer', [
      GET_PRODUCT_PIVOT_RULES,
      GET_OFFER_ITEM,
      GET_PRODUCT,
      GET_PRODUCT_LIST_UNIT_PRICES,
      GET_PRODUCT_TABLE,
      'getZoningRules',
    ]),
    offerType() {
      return this[GET_OFFER_ITEM]('type')
    },
    isTypeC() {
      return this.offerType === OFFER_TYPE.C
    },
    productTable() {
      return this[GET_PRODUCT_TABLE](this.id)
    },
    zoningRules() {
      return this.getZoningRules(this.id)
    },
    hasZonePricingRules() {
      return Object.keys(this.zoningRules).length > 0
    },
    pivotProductRules() {
      return this[GET_PRODUCT_PIVOT_RULES](this.id)
    },
    productsWithPivotRules() {
      return Object.keys(this.pivotProductRules.extraPricingRatio).concat(
        Object.keys(this.pivotProductRules.belowPricingRatio),
      )
    },
    hasPivotProductRules() {
      return (
        Object.values(this.pivotProductRules?.extraPricingRatio).length > 0 ||
        Object.values(this.pivotProductRules?.belowPricingRatio).length > 0
      )
    },
    isMaxDiscountLimitExceeded() {
      return Object.values(this.listMaxDiscountLimitExceededPerZone).some(Boolean)
    },
    isOfferInvalidForPrices() {
      return this.isOfferInvalidForZonePrices || this.isOfferInvalidForProductPrices
    },
    areDefaultWeights() {
      const product = this[GET_PRODUCT](this.id)
      return areDefaultProductWeights(product)
    },
  },
  methods: {
    updatePricesForC14E(priceData) {
      this[UPDATE_PRODUCT_ZONE_KG_PRICE]({
        productId: this.id,
        ...priceData,
      });
      this.validateZones();
    },
    updateIsMaxDiscountLimitExceeded(zoneId, isExceeded) {
      this.updateObjectReactiveProp(this.listMaxDiscountLimitExceededPerZone, zoneId, isExceeded)
    },
    updateObjectReactiveProp(list, key, value) {
      this.$set(list, key.toString(), value)
    },
    handlePriceChanged() {
      this.validateZones()

      this.productsWithPivotRules.forEach(productId => {
        this.$root.$emit('validatePivotProduct', productId)
      })
    },
    validateZones() {
      if (this.hasZonePricingRules) {
        let isOfferInvalidForZonePrices = false
        let isOfferInvalidForProductPrices = false

        const headers = this.productTable?.headers || []
        for (const zoneId of headers) {
          const zoneErrorsByWeight = getZoneErrors(zoneId, this)
          this.updateObjectReactiveProp(this.validationErrors, zoneId, zoneErrorsByWeight)

          const zoneWeightErrors = Object.values(zoneErrorsByWeight)

          if (zoneWeightErrors.some(this.isZoneErrorInZoneWeightErrorsList)) {
            isOfferInvalidForZonePrices = true
          }

          if (zoneWeightErrors.some(this.isProductErrorInZoneWeightErrorsList)) {
            isOfferInvalidForProductPrices = true
          }
        }

        this.isOfferInvalidForZonePrices = isOfferInvalidForZonePrices
        this.isOfferInvalidForProductPrices = isOfferInvalidForProductPrices
      }
    },
    isZoneErrorInZoneWeightErrorsList(zoneWeightErrorsList) {
      for (let i = 0; i < zoneWeightErrorsList.length; i++) {
        if (zoneWeightErrorsList[i].type === 'zone') {
          return true
        }
      }
      return false
    },
    isProductErrorInZoneWeightErrorsList(zoneWeightErrorsList) {
      for (let i = 0; i < zoneWeightErrorsList.length; i++) {
        if (zoneWeightErrorsList[i].type === 'product') {
          return true
        }
      }
      return false
    },
    emitIsOfferInvalidForZonePrices() {
      this.$emit('onChangeZoneOfferInvalidOnProduct', this.id, this.isOfferInvalidForZonePrices)
    },
    emitIsOfferInvalidForProductPrices() {
      this.$emit(
        'onChangeProductOfferInvalidOnProduct',
        this.id,
        this.isOfferInvalidForProductPrices,
      )
    },
  },
  mounted() {
    this.validateZones()

    this.emitIsOfferInvalidForZonePrices()
    this.emitIsOfferInvalidForProductPrices()
    if (this.id === 'C14E') {
      this.$root.$on('priceUpdatedFor24H', this.updatePricesForC14E);
    }
    this.$root.$on('validatePivotProduct', productId => {
      if (productId === this.id) {
        this.validateZones()
      }
    })
  },
  beforeDestroy() {
    if (this.id === 'C14E') {
      this.$root.$off('priceUpdatedFor24H', this.updatePricesForC14E);
    }
  },
  watch: {
    isOfferInvalidForZonePrices() {
      this.emitIsOfferInvalidForZonePrices()
    },
    isOfferInvalidForProductPrices() {
      this.emitIsOfferInvalidForProductPrices()
    },
    offerType() {
      this.validateZones()
    },
  },
}
</script>
